import classNames from 'classnames';
import React from 'react';

import checkboxCardStyles from '../CheckboxCard/checkbox-card.module.css';
import styles from './radio-card.module.css';

interface RadioCardProps {
    /** Shared by every option in the group. */
    name: string;
    value: string;
    label: string;
    /** Optional second line under the label. */
    hint?: string;
    checked: boolean;
    onSelect: (value: string) => void;
    disabled?: boolean;
    /** data-qa of the radio input. */
    dataQa?: string;
}

/**
 * One option of a radio group, as a card: the same look as CheckboxCard, with a round marker. A native radio input
 * underneath (restyled), so arrow keys move through the group and screen readers announce it as usual.
 */
function RadioCard({
    name,
    value,
    label,
    hint,
    checked,
    onSelect,
    disabled = false,
    dataQa,
}: RadioCardProps): React.ReactElement {
    const inputId = React.useId();
    const labelId = React.useId();
    const hintId = React.useId();

    return (
        <label
            htmlFor={inputId}
            className={classNames(checkboxCardStyles.Card, styles.Card, {
                [checkboxCardStyles.CardChecked]: checked,
                [checkboxCardStyles.CardDisabled]: disabled,
            })}
        >
            <input
                id={inputId}
                type="radio"
                name={name}
                value={value}
                checked={checked}
                onChange={() => onSelect(value)}
                disabled={disabled}
                aria-labelledby={labelId}
                aria-describedby={hint ? hintId : undefined}
                className={styles.Radio}
                data-qa={dataQa}
            />
            <span className={checkboxCardStyles.Copy}>
                <span className={checkboxCardStyles.Label} id={labelId}>
                    {label}
                </span>
                {hint && (
                    <span className={checkboxCardStyles.Hint} id={hintId}>
                        {hint}
                    </span>
                )}
            </span>
        </label>
    );
}

export default RadioCard;
